import fileSaver from 'file-saver';
import { useTranslation } from 'react-i18next';

import type { IdFileInfoDto } from '@/services/ws-file-uploader/src/main/resources/rest-api';
import { downloadFileWithAccessCheck } from '@/services/ws-file-uploader/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { useSubscribe } from '@/js/libs/observable';
import { useNotification } from '@/js/libs/ui/src/V2';
import { useLazyRequest } from '@/js/libs/use-request';

type UseDownloadFile = () => {
  downloadFile: (fileId: IdFileInfoDto, name?: string) => Promise<void>;
  pendingDownloadFile: boolean;
};

export const useDownloadFile: UseDownloadFile = () => {
  const notify = useNotification();
  const { t } = useTranslation(['backend', 'common']);

  const downloadRequest = useLazyRequest({
    apiMethod: downloadFileWithAccessCheck,
    cancelPreviousRequests: false,
  });

  const pendingDownloadFile = useSubscribe(downloadRequest.pending$);

  const download = useCallbackRef(async (fileId: IdFileInfoDto, name?: string) => {
    const { data, responseCode, headers } = await downloadRequest.load({ fileId });
    if (responseCode === 200) {
      fileSaver.saveAs(data, name ?? decodeURI(headers['x-audienzz-file-name']));
    }
    if (responseCode === 204) {
      notify.error({ title: t('common:ERROR'), body: t('common:NO_FILE') });
    }
  });

  return { downloadFile: download, pendingDownloadFile };
};
